import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, AlertCircle } from "lucide-react";

/**
 * BoostPackWidget
 *
 * Shown on the Account page (and reachable from the 402 error in any AI
 * flow). Lists the user's current boost balance + active packs + history,
 * and lets them buy more.
 *
 * Boost packs are NOT available on the Free tier (sign-up funnel protection).
 *
 * Props:
 *   autoOpen, when true, scrolls into view and triggers focus styling
 *                (used by the 402-error CTA path)
 *   variant, "full" (account page) | "compact" (inline error CTA)
 */
export default function BoostPackWidget({ autoOpen = false, variant = "full", userTier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const ref = React.useRef(null);

  const reload = async () => {
    try {
      const r = await api.get("/me/boosts");
      setData(r.data);
    } catch (e) {
      // noop, keep prior state
    }
  };

  useEffect(() => {
    let alive = true;
    api.get("/me/boosts")
      .then(r => alive && setData(r.data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (autoOpen && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [autoOpen]);

  const buy = async (packId) => {
    if (userTier === "free") {
      toast.error("Boost packs are available from Vault upwards. Upgrade your plan first.");
      return;
    }
    setBuying(true);
    try {
      const { data: resp } = await api.post("/billing/buy-boost", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      // Hop to Stripe Checkout
      window.location.href = resp.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout.");
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="card text-sm text-[#5a6b7a] flex items-center gap-2" data-testid="boost-widget-loading">
        <Loader2 size={14} className="animate-spin" /> Loading boost packs…
      </div>
    );
  }

  const balance = data?.balance ?? 0;
  const packs = data?.packs_for_sale ?? [];
  const activePacks = data?.active_packs ?? [];

  return (
    <div
      ref={ref}
      className={`card ${variant === "compact" ? "border-l-4 border-l-[#C9A961]" : ""} ${autoOpen ? "ring-2 ring-[#C9A961] ring-offset-2 ring-offset-[#FAF9F4]" : ""}`}
      data-testid="boost-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-serif text-xl text-[#0D2B52] flex items-center gap-1.5">
            <Sparkles size={15} strokeWidth={1.5} className="text-[#C9A961]" />
            AI Boost packs
          </h3>
          <p className="text-[12px] text-[#5a6b7a] mt-0.5">
            Out of monthly queries? Top up with extra credits. Each pack lasts 90 days from purchase.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-mono uppercase text-[#6B7280]">Balance</div>
          <div className="font-serif text-2xl text-[#1B6C62]" data-testid="boost-balance">{balance}</div>
        </div>
      </div>

      {/* Active packs */}
      {activePacks.length > 0 && (
        <div className="bg-[#E6F5F2] border border-[#1B6C62]/30 p-3 mb-3" data-testid="boost-active">
          <div className="label-mono text-[10px] text-[#1B6C62] mb-1.5">Your active packs</div>
          <ul className="space-y-1.5 text-[12px]">
            {activePacks.map(p => (
              <li key={p.id} className="flex items-center justify-between">
                <span className="text-[#0D2B52]">
                  <Check size={11} strokeWidth={2} className="inline -mt-0.5 mr-1 text-[#1B6C62]" />
                  {p.queries_remaining} of {p.queries_total} queries
                </span>
                <span className="font-mono text-[10px] text-[#6B7280]">
                  exp {new Date(p.expires_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Buy */}
      {userTier === "free" ? (
        <div className="bg-[#FAF6E8] border border-[#C9A961]/40 p-3 flex items-start gap-2 text-[12px]" data-testid="boost-free-notice">
          <AlertCircle size={13} className="text-[#8A6D3B] mt-0.5 shrink-0" />
          <span className="text-[#1A1A1A]">
            Boost packs aren&apos;t available on the Free plan. Upgrade to Vault (from $9.99/mo) to unlock them.
          </span>
        </div>
      ) : (
        <div className="grid sm:grid-cols-1 gap-2">
          {packs.map(p => (
            <button
              key={p.id}
              onClick={() => buy(p.id)}
              disabled={buying}
              className="text-left border border-[#E5E4DE] hover:border-[#1B6C62] p-3 bg-white transition-colors disabled:opacity-60 disabled:cursor-wait"
              data-testid={`boost-buy-${p.id}`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <div className="font-serif text-[15px] text-[#0D2B52]">{p.name}</div>
                <div className="font-mono text-[15px] text-[#1B6C62] font-bold">${p.amount}</div>
              </div>
              <div className="text-[11px] text-[#6B7280]">{p.blurb}</div>
            </button>
          ))}
        </div>
      )}

      <p className="text-[11px] text-[#6B7280] italic mt-3">
        Daily fair-use limits still apply to boost queries (anti-abuse). Boost queries are used automatically once your monthly allowance is gone, no manual switching.
      </p>
    </div>
  );
}
